import { useState, useEffect } from 'react';
import { Loader2, KeyRound, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function TokenDecryptabilityPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await diagnosticsAPI.tokenDecryptability();
      setData(d);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const allOk = data?.all_decryptable;
  const failCount = data?.failing_count || 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Token Decryptability</CardTitle>
          {!loading && data && (
            <Badge
              variant={allOk ? 'default' : 'destructive'}
              className={`text-[10px] ml-auto ${allOk ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}`}
            >
              {allOk ? 'ALL OK' : `${failCount} FAILING`}
            </Badge>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Checks whether stored integration tokens can be decrypted under the current encryption key.
          Failing tokens need a reconnect to re-mint under the active key.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">No data</p>
        ) : (
          <>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>Current key: <code className="font-mono">{data.current_key_id || 'none'}</code></span>
              <span>Source: <code className="font-mono">{data.key_source}</code></span>
              <span>Tokens checked: {data.total_tokens}</span>
            </div>

            {data.total_tokens === 0 ? (
              <p className="text-sm text-muted-foreground">No encrypted tokens found in the database.</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left px-3 py-1.5 font-medium">Token</th>
                      <th className="text-left px-3 py-1.5 font-medium">Format</th>
                      <th className="text-left px-3 py-1.5 font-medium">Stored Key</th>
                      <th className="text-left px-3 py-1.5 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tokens.map((t, i) => (
                      <tr key={i} className={`border-t ${!t.decryptable ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-1.5 font-mono">{t.label}</td>
                        <td className="px-3 py-1.5">{t.format}</td>
                        <td className="px-3 py-1.5 font-mono">{t.stored_key_id || '-'}</td>
                        <td className="px-3 py-1.5">
                          {t.decryptable ? (
                            <span className="inline-flex items-center gap-1 text-green-700">
                              <ShieldCheck className="h-3 w-3" /> ok
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-700">
                              <ShieldAlert className="h-3 w-3" /> {t.reason}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {failCount > 0 && (
              <p className="text-xs text-red-700 mt-2">
                {failCount} token(s) cannot be decrypted. Reconnect the affected integration(s)
                to re-mint tokens under the current key ({data.current_key_id}).
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
